import React from "react";
import { Stack, Typography } from "@mui/material";
import { formatCurrency } from "utils";
import { useFormContext, useWatch } from "react-hook-form";
import { DiscountType } from "@trnsact/trnsact-shared-types";
import { CurrencyInputField, SelectField, InterestInputField } from "components/form";
import { discountTypes } from "../../../../constants";
import { calcDiscount } from "../../../../lib/charges";
import { EquipmentFormValue } from "../../../../types";
import { FieldsContainer } from "../../../components/FieldsContainer";

export const Pricing = () => {
  const { control, getValues, setValue } = useFormContext<EquipmentFormValue>();
  const [customerPrice, discountType] = useWatch({
    control,
    name: ["calcPricing.customer.equipment", "commonInfo.discountType"],
  });

  const discountField = {
    [DiscountType.Flat]: CurrencyInputField,
    [DiscountType.Percent]: InterestInputField,
  };

  const DiscountFieldComponent = discountField[discountType];

  const calcTotals = () => {
    const {
      calcPricing: { customer, wholesale, retail },
      commonInfo: { retailPrice, discountValue, discountType, saleCost },
    } = getValues();

    const customerPriceWithDiscount = calcDiscount(retailPrice, discountValue ?? 0, discountType);

    setValue("calcPricing.retail.total", retailPrice + retail.attachments);
    setValue("calcPricing.wholesale.total", saleCost + wholesale.attachments);

    setValue("calcPricing.customer.equipment", customerPriceWithDiscount);
    setValue("calcPricing.customer.total", customer.attachments + customerPriceWithDiscount);
  };

  return (
    <Stack gap="1rem">
      <Typography variant="subtitle2" component="span">
        Pricing
      </Typography>

      <FieldsContainer columns={5}>
        <CurrencyInputField
          control={control}
          label="Wholesale Cost"
          name="commonInfo.saleCost"
          textFieldProps={{ variant: "outlined" }}
          customOnChange={(nextValue, name) => {
            setValue(name, nextValue);
            calcTotals();
          }}
        />

        <CurrencyInputField
          control={control}
          label="Total Retail Price"
          name="commonInfo.retailPrice"
          textFieldProps={{ variant: "outlined" }}
          customOnChange={(nextValue, name) => {
            setValue(name, nextValue);
            calcTotals();
          }}
        />

        <SelectField
          control={control}
          variant="outlined"
          label="Discount Type"
          options={discountTypes}
          extraAction={calcTotals}
          name="commonInfo.discountType"
        />

        <DiscountFieldComponent
          control={control}
          label="Discount"
          name="commonInfo.discountValue"
          textFieldProps={{ variant: "outlined" }}
          customOnChange={(nextValue, name) => {
            setValue(name, nextValue);
            calcTotals();
          }}
        />
      </FieldsContainer>

      <Typography variant="subtitle2" component="span">
        Customer Price: {formatCurrency(customerPrice, true)}
      </Typography>
    </Stack>
  );
};
